import type { ReactNode } from 'react';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { BotonCerrarHoja } from '#components/common/ModalHoja';

import { Hoja } from './Hoja';
import { Icono } from './Icono';
import type { NombreIcono } from './Icono';
import { color, densidad, radio, suave, TACTIL, texto } from './tokens';

/** Una fila de la hoja de acciones. */
export type FilaAccion = {
  id: string;
  texto: string;
  icono: NombreIcono;
  /** Línea pequeña bajo el texto (p. ej. el avance de las notas). */
  detalle?: string;
  /** En rojo (Borrar). */
  peligro?: boolean;
  /** Apagada: se ve pero no se puede tocar (explica por qué en `detalle`). */
  deshabilitada?: boolean;
  /** Cifra a la derecha (p. ej. lo que se asignaría). */
  valor?: ReactNode;
  /** Separa esta fila de las anteriores con más aire (grupo nuevo). */
  grupoNuevo?: boolean;
  onPress: () => void;
};

type HojaAccionesProps = {
  titulo: string;
  /** Emoji de la categoría o del grupo, en su cajita a la izquierda. */
  emoji?: string | null;
  /** Línea bajo el título (p. ej. «Disponible 120,00 €»). */
  subtitulo?: ReactNode;
  filas: FilaAccion[];
  onCerrar: () => void;
  /** Contenido en lugar de las filas (p. ej. el campo de renombrar). */
  children?: ReactNode;
  'data-testid'?: string;
};

/**
 * Hoja de acciones común (B6 · H35) para los menús de grupo, categoría y
 * mes: cabecera con emoji, nombre y Disponible, y filas de 52 px con icono.
 * Sin espacio en blanco ni «Sin notas»: las notas son una fila más.
 */
export function HojaAcciones({
  titulo,
  emoji,
  subtitulo,
  filas,
  onCerrar,
  children,
  'data-testid': testId,
}: HojaAccionesProps) {
  return (
    <Hoja aria-label={titulo} data-testid={testId} onCerrar={onCerrar}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          marginBottom: 8,
          flexShrink: 0,
        }}
      >
        {emoji && (
          <View
            aria-hidden
            style={{
              width: 44,
              height: 44,
              borderRadius: radio.sm,
              backgroundColor: color.surface2,
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              flexShrink: 0,
            }}
          >
            {emoji}
          </View>
        )}
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            role="heading"
            aria-level={2}
            style={{
              fontSize: 20,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              color: color.fg,
              overflowWrap: 'anywhere',
            }}
          >
            {titulo}
          </Text>
          {subtitulo && (
            <Text
              style={{
                ...texto.secundario,
                fontWeight: 600,
                color: color.fg2,
                marginTop: 2,
              }}
            >
              {subtitulo}
            </Text>
          )}
        </View>
        <BotonCerrarHoja onPress={onCerrar} />
      </View>
      {children ??
        filas.map((fila, i) => (
          <Button
            key={fila.id}
            variant="bare"
            data-testid={`accion-${fila.id}`}
            isDisabled={fila.deshabilitada}
            onPress={fila.onPress}
            style={{
              minHeight: Math.max(TACTIL, 52),
              justifyContent: 'flex-start',
              gap: 14,
              padding: '6px 4px',
              borderRadius: radio.sm,
              borderTop: i > 0 ? `1px solid ${color.line}` : undefined,
              marginTop: i > 0 && fila.grupoNuevo ? 8 : 0,
              color: fila.deshabilitada
                ? color.fg4
                : fila.peligro
                  ? color.bad
                  : color.fg,
              backgroundColor: 'transparent',
              textAlign: 'left',
            }}
          >
            <View
              aria-hidden
              style={{
                width: 36,
                height: 36,
                borderRadius: radio.sm,
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                backgroundColor: fila.peligro
                  ? suave(color.bad)
                  : color.surface2,
              }}
            >
              <Icono nombre={fila.icono} size={18} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ ...texto.fila, color: 'inherit' }}>
                {fila.texto}
              </Text>
              {fila.detalle && (
                <Text
                  style={{
                    ...densidad.pequeno,
                    color: color.fg2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {fila.detalle}
                </Text>
              )}
            </View>
            {fila.valor != null && (
              <Text
                style={{
                  ...densidad.cifra,
                  fontWeight: 800,
                  flexShrink: 0,
                  color: fila.deshabilitada ? color.fg4 : color.accent,
                }}
              >
                {fila.valor}
              </Text>
            )}
          </Button>
        ))}
    </Hoja>
  );
}
